import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';

import React, { useMemo, useCallback } from 'react';
import { TransactionCategory } from '@shipstation/financial-services-ui-components';
import { FormattedMessage } from 'react-intl';

import { FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { PaymentMethodPicker } from './payment-method-picker';
import { sourceString } from './payments-table-auctane-pay';
import { BillingPlanIds } from '../../../../../enums/billing-plan-ids';
import { SellerSettings } from '../../../../../enums/seller-settings/seller-settings';
import { useSellerSetting } from '../../../../../hooks/use-seller-setting';
import { useCanShowShipsurance } from '../../../../../services/insurance-service';
import { useWallet } from '../../../../../wallet/hooks/use-wallet';
import { usePaymentAndSubscriptionMegaContext } from '../../context';
import { PageEvents, useTrackEvent } from '../../hooks/use-track-event';
import { paymentTableMessages } from '../../messages';
import style from '../../style.module.less';
import { ChangeBillingInfoButton } from '../change-billing-info-button';
import { ChangeSubscriptionButtonWrapper } from '../change-subscription-button-wrapper';

export const SubscriptionRow = () => {
  const sellerShipsuranceEligible = useCanShowShipsurance();
  const { isWalletRegistered } = useWallet();
  const balanceMessage = useMemo(
    () => (sellerShipsuranceEligible ? paymentTableMessages.shipsuranceBalance : paymentTableMessages.subscriptionBalance),
    [sellerShipsuranceEligible],
  );
  const nicknameSellerSetting = useSellerSetting(SellerSettings.recurlyPaymentNickname);

  const { billingInfo, currentBillingPlanId, hasLegacyRecurlyBillingInfo, isMissingPaymentMethod, paymentMethodDetails } =
    usePaymentAndSubscriptionMegaContext();
  const { creditCard, address } = billingInfo ?? {};

  const paymentMethodStringBuilder = useCallback(() => {
    if (nicknameSellerSetting) {
      return nicknameSellerSetting;
    }
    if (creditCard?.cardType && creditCard?.lastFour) {
      return `${creditCard.cardType}-${creditCard.lastFour}`;
    }
    return address?.name;
  }, [address?.name, creditCard?.cardType, creditCard?.lastFour, nicknameSellerSetting]);

  const trackRecurlyBillingConfirmClick = useTrackEvent(PageEvents.UPDATE_RECURLY_BILLING_SAVE, sourceString);
  const trackRecurlyBillingClick = useTrackEvent(PageEvents.UDPATE_RECURLY_BILLING_CLICK, sourceString);

  const hasUnpaidPlan = useMemo(() => {
    return !currentBillingPlanId || [BillingPlanIds.Trial, BillingPlanIds.Free2025Q2].includes(Number(currentBillingPlanId));
  }, [currentBillingPlanId]);

  const showChoosePlanButton = !hasLegacyRecurlyBillingInfo && (isMissingPaymentMethod || hasUnpaidPlan);
  const showPaymentMethodPicker = !hasLegacyRecurlyBillingInfo && paymentMethodDetails.length > 0 && !showChoosePlanButton;

  return (
    <>
      <FlexRow className={style['payment-row']}>
        <div className={style['tooltip-wrapper']}>
          <Text className={style['payment-row-label']} type={TextType.Label} value={balanceMessage} />
          {sellerShipsuranceEligible ? (
            <Tooltip position='top' text={<FormattedMessage {...paymentTableMessages.subscriptionAndShipsuranceTooltip} />}>
              <div className={style['menu-toggle-contents']}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </div>
            </Tooltip>
          ) : null}
        </div>
        {showChoosePlanButton ? (
          <ChangeSubscriptionButtonWrapper
            onClickTrack={trackRecurlyBillingClick}
            theme={isWalletRegistered ? THEME_CONSTANTS.secondary : THEME_CONSTANTS.tertiary}
          >
            <FormattedMessage {...paymentTableMessages.choosePlan} />
          </ChangeSubscriptionButtonWrapper>
        ) : null}
        {hasLegacyRecurlyBillingInfo ? (
          <>
            <div>{paymentMethodStringBuilder()}</div>
            <ChangeBillingInfoButton
              onClickTrack={trackRecurlyBillingClick}
              onConfirmClickTrack={trackRecurlyBillingConfirmClick}
              theme={THEME_CONSTANTS.tertiary}
            >
              <FormattedMessage {...paymentTableMessages.updatePaymentMethod} />
            </ChangeBillingInfoButton>
          </>
        ) : null}
        {showPaymentMethodPicker ? <PaymentMethodPicker transactionCategory={TransactionCategory.Subscription} /> : null}
      </FlexRow>
    </>
  );
};
